import type { ReactNode } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export type PolicyFormState = {
  patronTypeId: string;
  itemTypeId: string;
  branchId: string;
  loanPeriodDays: number;
  renewalLimit: number;
  fineRatePerDay: number;
  gracePeriodDays: number;
  maxFineAmount: string;
  isActive: boolean;
};

type TypeOption = { id: string; name: string; code?: string };

type CirculationPolicyDialogProps = {
  open: boolean;
  editing: boolean;
  saving: boolean;
  form: PolicyFormState;
  patronTypes: TypeOption[];
  itemTypes: TypeOption[];
  branches: TypeOption[];
  onOpenChange: (open: boolean) => void;
  onChange: (next: PolicyFormState) => void;
  onSave: () => void;
};

export function CirculationPolicyDialog({
  open,
  editing,
  saving,
  form,
  patronTypes,
  itemTypes,
  branches,
  onOpenChange,
  onChange,
  onSave,
}: CirculationPolicyDialogProps) {
  const set = <K extends keyof PolicyFormState>(key: K, value: PolicyFormState[K]) =>
    onChange({ ...form, [key]: value });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit" : "New"} circulation policy</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-1">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field>
              <Label required>Patron type</Label>
              <Select
                value={form.patronTypeId}
                onValueChange={(v) => set("patronTypeId", v)}
                disabled={editing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {patronTypes.map((pt) => (
                    <SelectItem key={pt.id} value={pt.id}>
                      {pt.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <Label required>Item type</Label>
              <Select
                value={form.itemTypeId}
                onValueChange={(v) => set("itemTypeId", v)}
                disabled={editing}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  {itemTypes.map((it) => (
                    <SelectItem key={it.id} value={it.id}>
                      {it.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field>
            <Label>Branch</Label>
            <Select
              value={form.branchId || "__all__"}
              onValueChange={(v) => set("branchId", v === "__all__" ? "" : v)}
              disabled={editing}
            >
              <SelectTrigger>
                <SelectValue placeholder="All branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All branches</SelectItem>
                {branches.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500">Leave as all branches to apply tenant-wide.</p>
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Loan period (days)</Label>
              <Input
                type="number"
                min={1}
                value={form.loanPeriodDays}
                onChange={(e) => set("loanPeriodDays", parseInt(e.target.value) || 1)}
              />
            </Field>
            <Field>
              <Label>Renewal limit</Label>
              <Input
                type="number"
                min={0}
                value={form.renewalLimit}
                onChange={(e) => set("renewalLimit", parseInt(e.target.value) || 0)}
              />
            </Field>
            <Field>
              <Label>Fine rate / day (₦)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.fineRatePerDay}
                onChange={(e) => set("fineRatePerDay", parseFloat(e.target.value) || 0)}
              />
            </Field>
            <Field>
              <Label>Grace period (days)</Label>
              <Input
                type="number"
                min={0}
                value={form.gracePeriodDays}
                onChange={(e) => set("gracePeriodDays", parseInt(e.target.value) || 0)}
              />
            </Field>
          </div>

          <Field>
            <Label>Max fine cap (₦)</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={form.maxFineAmount}
              onChange={(e) => set("maxFineAmount", e.target.value)}
              placeholder="No cap"
            />
          </Field>

          <div className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50/80 px-3 py-2.5">
            <Checkbox
              id="policyActive"
              checked={form.isActive}
              onCheckedChange={(v) => set("isActive", v === true)}
            />
            <Label htmlFor="policyActive" className="cursor-pointer font-normal">
              Policy is active
            </Label>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-1.5 h-4 w-4" />
            )}
            Save policy
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-1.5">{children}</div>;
}
